import * as T from 'three';
import {lidPose,steamPuffs,REVEAL_SECONDS} from './steamer.js';
import {GlitterField} from './glitter.js';
import {PowderCoat} from './powder-coat.js';
import {FurComb} from './fur-comb.js';
import {createFurShells,furColors} from './fur-shells.js';
import {applyPowderShading} from './powder-shading.js';
import {variantGlitter,variantMaterial} from './variants.js';
import {hasPowder} from './catalog.js';
// Per-toy extras that sit on top of the soft body: a steamer reveal, glitter
// in translucent dough, a powder coat on sticky mochi, and stroked fur.
// The stage calls step/peel/reset/dispose; every part is optional.
const GLITTER={steambun:{count:110,colors:['#eef3ff','#ffffff','#d6e2f7'],iridescence:.4,size:.075}};
// A soft round puff with a feathered edge, made without a canvas.
function softPuffTexture(){
  const size=64,data=new Uint8Array(size*size*4);
  for(let y=0;y<size;y++)for(let x=0;x<size;x++){
    const d=Math.hypot(x-size/2+.5,y-size/2+.5)/(size/2),a=Math.max(0,1-d)**2;
    data.set([255,255,255,Math.round(a*255)],(y*size+x)*4);
  }
  const texture=new T.DataTexture(data,size,size);texture.needsUpdate=true;return texture;
}
const boxOf=items=>{const box=new T.Box3();for(const item of items){item.mesh.geometry.computeBoundingBox();box.union(item.mesh.geometry.boundingBox);}return box;};

export function createToyExtras(stage,record){
  const parts=[],kind=record.kind,meshes=stage.meshes;
  let peelTimer=0,lastTarget=null;

  // Steamer: the lid tier starts seated on the steamer over the bun, rises until
  // its rim clears the bun top, moves across while raised, then lowers.
  const lid=meshes.filter(item=>item.mesh.name.startsWith('prop_lid'));
  const steamer=meshes.filter(item=>item.mesh.name.startsWith('prop_steamer'));
  if(lid.length&&steamer.length){
    const base=boxOf(steamer),lidBox=boxOf(lid),bunBox=boxOf(meshes.filter(item=>!item.isProp));
    const rest=lidBox.getCenter(new T.Vector3());
    const closed=new T.Vector3((base.min.x+base.max.x)/2,rest.y+base.max.y-lidBox.min.y+.004,(base.min.z+base.max.z)/2);
    const radius=(lidBox.max.x-lidBox.min.x)/2;
    // Raised high enough that even the tilted rim passes over the bun.
    const raise=Math.max(0,bunBox.max.y-base.max.y)+radius*Math.sin(.1)+.08;
    const across=new T.Vector3(rest.x-closed.x,0,rest.z-closed.z).normalize(),axis=new T.Vector3(across.z,0,-across.x);
    for(const item of lid){item.mesh.geometry.translate(-rest.x,-rest.y,-rest.z);item.mesh.position.copy(rest);}
    const top=new T.Vector3(closed.x,base.max.y,closed.z),bunRadius=(bunBox.max.x-bunBox.min.x)/2;
    const puffMaterial=new T.SpriteMaterial({map:softPuffTexture(),color:'#ffffff',transparent:true,opacity:0,depthWrite:false,toneMapped:false});
    const puffs=Array.from({length:9},()=>{const m=new T.Sprite(puffMaterial.clone());m.visible=false;stage.toy.add(m);return m;});
    let t=0;
    const apply=()=>{
      const pose=lidPose(t);
      const x=closed.x+(rest.x-closed.x)*pose.travel,z=closed.z+(rest.z-closed.z)*pose.travel;
      const y=pose.drop>0?closed.y+raise+(rest.y-closed.y-raise)*pose.drop:closed.y+raise*pose.rise;
      for(const item of lid){item.mesh.position.set(x,y,z);item.mesh.quaternion.setFromAxisAngle(axis,pose.tilt);}
      steamPuffs(t,puffs.length).forEach((p,i)=>{
        const m=puffs[i];m.visible=p.opacity>0;m.material.opacity=p.opacity;
        m.position.set(top.x+p.x*bunRadius,bunBox.max.y*.7+p.y*bunRadius,top.z+p.z*bunRadius);m.scale.setScalar(Math.max(.001,p.scale*bunRadius));
      });
    };
    apply();
    // Props frame nothing: the play area fits the bun, not the steamer and lid.
    for(const item of [...lid,...steamer])item.mesh.geometry.boundingBox=new T.Box3();
    parts.push({
      get active(){return t<REVEAL_SECONDS+2.2;},
      step(dt){t+=dt;apply();},
      reset(){},
      dispose(){for(const m of puffs){m.material.dispose();stage.toy.remove(m);}puffMaterial.map.dispose();puffMaterial.dispose();},
    });
  }

  // Glitter: flakes bound inside the dough follow it with a lag and glint as they drift.
  const glitterConfig=GLITTER[kind]?{...GLITTER[kind],...variantGlitter(kind,record.variant)}:null;
  const dough=meshes.filter(item=>/^body_jelly_bun/.test(item.mesh.name));
  if(glitterConfig&&dough.length){
    const box=boxOf(dough),center=box.getCenter(new T.Vector3()),size=box.getSize(new T.Vector3());
    const field=new GlitterField({count:glitterConfig.count,center:center.toArray(),radii:[size.x*.36,size.y*.36,size.z*.36],seed:9});
    const bindings=field.points.map(p=>stage.body.bind(p));
    const material=new T.MeshPhysicalMaterial({metalness:1,roughness:.22,side:T.DoubleSide,iridescence:glitterConfig.iridescence??0,emissive:'#ffffff',emissiveIntensity:.5});
    const flakes=new T.InstancedMesh(new T.CircleGeometry(glitterConfig.size*.55,6),material,field.count);
    flakes.name='glitter';flakes.frustumCulled=false;
    const colors=glitterConfig.colors.map(c=>new T.Color(c));
    for(let i=0;i<field.count;i++)flakes.setColorAt(i,colors[i%colors.length]);
    stage.toy.add(flakes);
    const matrix=new T.Matrix4(),q=new T.Quaternion(),e=new T.Euler(),one=new T.Vector3(1,1,1),p=new T.Vector3();
    const write=()=>{
      for(let i=0;i<field.count;i++){
        e.set(...field.angles[i]);q.setFromEuler(e);p.fromArray(field.positions[i]);
        flakes.setMatrixAt(i,matrix.compose(p,q,one));
      }
      flakes.instanceMatrix.needsUpdate=true;material.emissiveIntensity=.5+1.2*field.sparkle;
    };
    write();
    parts.push({
      get active(){return field.sparkle>.003||field.settle<.98;},
      step(dt){field.step(dt,bindings.map(b=>stage.body.sample(b,[0,0,0])));write();},
      reset(){},
      dispose(){flakes.geometry.dispose();material.dispose();stage.toy.remove(flakes);},
    });
  }

  // Powder: sticky bare mochi; dusting whitens and dries it; rubbing wipes it off.
  const skin=hasPowder(kind)?meshes.filter(item=>/^body_mochi_kona/.test(item.mesh.name)):[];
  const coats=skin.map(item=>{
    const box=boxOf([item]),center=box.getCenter(new T.Vector3()).toArray(),radius=box.getSize(new T.Vector3()).length()/2;
    const coat=new PowderCoat({rest:item.rest,center,radius,seed:5});coat.prime();
    const shown=coat.powder.slice(),attribute=new T.BufferAttribute(shown,1);
    const geometry=item.mesh.geometry;geometry.setAttribute('powder',attribute);
    if(!geometry.getAttribute('restPosition'))geometry.setAttribute('restPosition',new T.BufferAttribute(item.rest.slice(),3));
    // Bare mochi is wet-looking and glossy; powder takes the coat away.
    if(item.mesh.material.isMeshPhysicalMaterial)Object.assign(item.mesh.material,{clearcoat:.65,clearcoatRoughness:.12,roughness:Math.min(item.mesh.material.roughness,.26)});
    applyPowderShading(item.mesh.material);
    return {coat,shown,attribute};
  });
  // A pat of the puff sends fine powder drifting down onto the mochi.
  const dustTexture=coats.length?softPuffTexture():null,specks=[];
  const skinBox=coats.length?boxOf(skin):null;
  const spawnDust=()=>{
    const c=skinBox.getCenter(new T.Vector3()),size=skinBox.getSize(new T.Vector3());
    for(let i=0;i<70;i++){
      const sprite=new T.Sprite(new T.SpriteMaterial({map:dustTexture,color:'#ffffff',transparent:true,depthWrite:false,opacity:0,toneMapped:false}));
      const a=Math.random()*Math.PI*2,r=Math.sqrt(Math.random())*.45;
      sprite.userData={x:c.x+Math.cos(a)*r*size.x,z:c.z+Math.abs(Math.sin(a))*r*size.z*.8,y:skinBox.max.y+size.y*(.5+Math.random()*.7),fall:.6+Math.random()*.5,age:-Math.random()*.25,life:1+Math.random()*.5,size:size.x*(.035+Math.random()*.05)};
      stage.toy.add(sprite);specks.push(sprite);
    }
  };
  if(coats.length)parts.push({
    moving:false,
    get active(){return this.moving||specks.length>0;},
    step(dt){
      for(let i=specks.length-1;i>=0;i--){
        const s=specks[i],d=s.userData;d.age+=dt;
        if(d.age>=d.life){stage.toy.remove(s);s.material.dispose();specks.splice(i,1);continue;}
        const k=Math.max(0,d.age)/d.life;
        s.position.set(d.x,d.y-d.fall*k*(skinBox.max.y-skinBox.min.y)*1.6,d.z);s.scale.setScalar(d.size);
        s.material.opacity=d.age<0?0:Math.min(1,1.6*Math.sin(Math.PI*k));
      }
      const hold=stage.body?.hold,target=stage.dragTarget;
      if(hold&&target&&lastTarget){
        const motion=Math.hypot(...target.map((n,a)=>n-lastTarget[a]));
        if(motion>1e-4)for(const {coat} of coats)coat.rub(stage.grabPoint,Math.min(dt,motion*3));
      }
      // Shown powder eases toward the coat so a pat drifts down instead of popping in.
      let moving=false;const k=1-Math.exp(-dt/.18);
      for(const {coat,shown,attribute} of coats){
        let changed=false;
        for(let i=0;i<shown.length;i++){const d=coat.powder[i]-shown[i];if(Math.abs(d)>.002){shown[i]+=d*k;changed=true;}else if(d){shown[i]=coat.powder[i];changed=true;}}
        if(changed){attribute.needsUpdate=true;moving=true;}
      }
      this.moving=moving;
    },
    reset(){for(const {coat,shown,attribute} of coats){coat.restore();shown.set(coat.powder);attribute.needsUpdate=true;}},
    dispose(){for(const s of specks){stage.toy.remove(s);s.material.dispose();}specks.length=0;dustTexture?.dispose();},
  });

  // Fur: shells over the body; stroking combs the fur and leaves a trail.
  const furItems=meshes.filter(item=>/^body_fur/.test(item.mesh.name));
  const faceParts=meshes.filter(item=>/^face_(eye|nose)/.test(item.mesh.name));
  const faceCenter=faceParts.length?boxOf(faceParts).getCenter(new T.Vector3()):null;
  const furs=furItems.map(item=>{
    const comb=new FurComb({rest:item.rest,normals:item.mesh.geometry.attributes.normal.array.slice()});
    // Fur thins out around the eyes and nose so the face stays readable.
    const height=boxOf([item]).getSize(new T.Vector3()).y,lengths=new Float32Array(item.rest.length/3).fill(1);
    if(faceCenter)for(let i=0;i<lengths.length;i++){
      const d=Math.hypot(item.rest[i*3]-faceCenter.x,item.rest[i*3+1]-faceCenter.y,item.rest[i*3+2]-faceCenter.z)/height;
      lengths[i]=Math.min(1,Math.max(.22,(d-.08)/.2));
    }
    const source=item.sourceMaterial,rare=variantMaterial(kind,record.variant,source.name)?.base??null;
    const shells=createFurShells(item.mesh,comb,{lengths,...furColors(source.color.toArray(),rare)});stage.toy.add(shells.mesh);
    return {comb,shells};
  });
  if(furs.length)parts.push({
    combing:false,
    get active(){return this.combing;},
    step(dt){
      const hold=stage.body?.hold,target=stage.dragTarget;this.combing=false;
      if(hold&&target&&lastTarget){
        const motion=target.map((n,a)=>n-lastTarget[a]);
        if(Math.hypot(...motion)>1e-4){for(const {comb} of furs)comb.brush(stage.grabPoint.map((n,a)=>n+(target[a]-stage.grabPoint[a])*.5),motion,.45);this.combing=true;}
      }
      for(const {comb,shells} of furs){comb.relax(dt);shells.update();}
    },
    reset(){for(const {comb,shells} of furs){comb.comb.set(comb.droop);comb.changed=true;shells.update();}},
    dispose(){for(const {shells} of furs){shells.dispose();stage.toy.remove(shells.mesh);}},
  });

  return {
    get active(){return peelTimer>0||parts.some(part=>part.active);},
    step(dt){
      for(const part of parts)part.step(dt);
      lastTarget=stage.body?.hold&&stage.dragTarget?[...stage.dragTarget]:null;
      if(peelTimer>0){peelTimer-=dt;if(peelTimer<=0){peelTimer=0;stage.body?.release();}}
    },
    // Sticky skin holds on for a moment after the finger lifts, then lets go.
    peel(){
      if(!coats.length||!stage.body?.hold||!stage.grabPoint)return false;
      const stick=Math.min(...coats.map(({coat})=>coat.stickiness(stage.grabPoint)));
      if(stick<.4)return false;
      peelTimer=.12+.38*stick;return true;
    },
    dust(){if(!coats.length)return false;for(const {coat} of coats)coat.dust();spawnDust();return true;},
    reset(){peelTimer=0;for(const part of parts)part.reset();},
    dispose(){for(const part of parts)part.dispose();},
  };
}
